import type { TOrgInfo } from "@/types";
import { useSearchAccountsMutation } from "@api/accounts";
import { useAppSelector } from "@store";
import { getAccount } from "@store/account/selectors";
import type { DebouncedFunc } from "lodash";
import debounce from "lodash.debounce";
import type React from "react";
import {
  type ReactNode,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
} from "react";

type CurrentAccountContextType = {
  accounts: any[] | undefined;
  isUninitialized: boolean;
  account: TOrgInfo | null;
  handleSearchAccounts: DebouncedFunc<(query: string) => Promise<void>>;
  isLoading: boolean;
  isError: boolean;
  currentAccount: TOrgInfo | null;
  searchValue: string;
  setSearchValue: (value: string) => void;
  setIsLoading: (isLoading: boolean) => void;
  setAccount: (account: TOrgInfo | null) => void;
  isDifferentFromCurrentAccount: boolean;
};

const CurrentAccountContext = createContext<
  CurrentAccountContextType | undefined
>(undefined);

export const useCurrentAccount = (): CurrentAccountContextType => {
  const context = useContext(CurrentAccountContext);
  if (!context) {
    throw new Error(
      "useCurrentAccount must be used within a CurrentAccountProvider",
    );
  }
  return context;
};

type CurrentAccountProviderProps = {
  children: ReactNode;
};

export const CurrentAccountProvider: React.FC<CurrentAccountProviderProps> = ({
  children,
}) => {
  "use no memo";
  const [searchValue, setSearchValue] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [searchAccounts, { data: accounts, isUninitialized, isError }] =
    useSearchAccountsMutation();
  const currentAccount = useAppSelector(getAccount);
  const [account, setAccount] = useState<TOrgInfo | null>(null);
  const [isDifferentFromCurrentAccount, setIsDifferentFromCurrentAccount] =
    useState(false);

  useEffect(() => {
    if (currentAccount) setAccount(currentAccount);
  }, [!!currentAccount]);

  const handleSearchAccounts = useCallback(
    debounce(async (query: string) => {
      setIsLoading(true);
      await searchAccounts({ search: query });
      setIsLoading(false);
    }, 500),
    [],
  );

  useEffect(() => {
    setIsDifferentFromCurrentAccount(currentAccount?._id !== account?._id);
  }, [currentAccount, account]);

  const value = {
    accounts,
    isUninitialized,
    account,
    handleSearchAccounts,
    isLoading,
    searchValue,
    setSearchValue,
    setIsLoading,
    setAccount,
    isError,
    currentAccount,
    isDifferentFromCurrentAccount,
  };

  return (
    <CurrentAccountContext.Provider value={value}>
      {children}
    </CurrentAccountContext.Provider>
  );
};

export default CurrentAccountProvider;
